import type { SoundBank } from './SoundBank';

/**
 * PositionalEmitter — persistent looping sources whose world position
 * updates each frame.
 *
 * Distinct from SoundBank.play({ position }) (one-shot, position fixed
 * at fire time) and AmbientMixer (continuous beds with NO position —
 * just listener-relative globals like wind/forest). Use this for
 * objects that emit sustained sound while moving in world space —
 * the canonical case is the delivery truck's motor that should pan
 * left as the truck drives off-screen left and muffle as it gets far.
 *
 * Usage:
 *   const emitters = new PositionalEmitter(bank);
 *   // Start a per-truck motor loop. posFn is invoked every frame —
 *   // return null to fade the emitter to silence (e.g. truck parked).
 *   emitters.start('truck-motor', 'truck/motor', () => truck.isMoving
 *     ? { x: truck.x, z: truck.z } : null, 0.55);
 *   // ... in your rAF tick:
 *   emitters.update(dt);
 *   // Stop + dispose when done:
 *   emitters.stop('truck-motor');
 *
 * Smoothing: gain / pan / lowpass values are tweened toward their
 * target each frame so a fast-moving emitter doesn't zip across the
 * stereo field in a single audio block (which clicks).
 */
export class PositionalEmitter {
  private ctx: AudioContext;
  private bank: SoundBank;
  private master: GainNode;
  private emitters = new Map<string, Emitter>();

  /** How fast smoothed values chase their target — per second. 8/s
   *  means ~125ms response time, fast enough to feel reactive on a
   *  moving truck without clicking. */
  private static readonly SMOOTH_PER_S = 8;

  constructor(bank: SoundBank) {
    this.bank = bank;
    this.ctx = bank.audioContext();
    this.master = bank.masterBus();
  }

  /** Start a continuous looping emitter. Idempotent — calling start()
   *  with a name that's already running is a no-op so the same event
   *  can fire repeatedly without leaking sources.
   *
   *  `posFn` is invoked every frame. Return:
   *    - `{ x, z }`  emit position in world space → spatial chain updates
   *    - `null`      emitter exists but is silent (faded to 0, source
   *                  kept alive; cheaper than start/stop churn for
   *                  intermittent emitters)
   *
   *  Returns false if the group isn't loaded yet (caller can retry). */
  start(name: string, group: string,
        posFn: () => { x: number; z: number } | null,
        baseVolume = 0.5): boolean {
    if (this.emitters.has(name)) return true;
    const entry = this.bank.randomBuffer(group);
    if (!entry) return false;

    const src = this.ctx.createBufferSource();
    src.buffer = entry.buf;
    src.loop = true;
    // Random offset so two emitters using the same buffer don't lock
    // phase and create comb-filter artifacts.
    const offset = Math.random() * entry.buf.duration;

    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    const lpf = this.ctx.createBiquadFilter();
    lpf.type = 'lowpass';
    lpf.frequency.value = 22000;
    lpf.Q.value = 0.7;
    const pan = this.ctx.createStereoPanner();
    pan.pan.value = 0;

    src.connect(gain).connect(lpf).connect(pan).connect(this.master);
    src.start(0, offset);

    this.emitters.set(name, {
      group, src, gain, lpf, pan,
      bufferGain: entry.gain,
      posFn,
      baseVolume,
      curGain: 0, curPan: 0, curHz: 22000,
    });
    return true;
  }

  /** Stop and dispose. Fades over ~100ms to avoid clicking. Subsequent
   *  start() with the same name spawns a fresh source. */
  stop(name: string): void {
    const em = this.emitters.get(name);
    if (!em) return;
    const t = this.ctx.currentTime;
    em.gain.gain.cancelScheduledValues(t);
    em.gain.gain.setValueAtTime(em.gain.gain.value, t);
    em.gain.gain.linearRampToValueAtTime(0, t + 0.1);
    try { em.src.stop(t + 0.12); } catch { /* already stopped */ }
    this.emitters.delete(name);
  }

  /** Is this named emitter currently active? */
  has(name: string): boolean { return this.emitters.has(name); }

  /** Stop all emitters. Used on dispose / reset. */
  stopAll(): void {
    for (const name of Array.from(this.emitters.keys())) this.stop(name);
  }

  /** Per-frame update — read each emitter's current position and tween
   *  its spatial parameters. Cheap (linear in emitter count); safe to
   *  call every frame.
   *
   *  Call from the same rAF loop that drives AmbientMixer.update(). */
  update(dt: number): void {
    const k = Math.min(1, dt * PositionalEmitter.SMOOTH_PER_S);
    for (const em of this.emitters.values()) {
      const pos = em.posFn();
      let targetGain: number;
      let targetPan: number;
      let targetHz: number;
      if (!pos) {
        // Fade to silence but keep the source alive.
        targetGain = 0;
        targetPan = em.curPan;
        targetHz = em.curHz;
      } else {
        const s = this.bank.spatialFactors(pos);
        targetGain = s.gain * em.baseVolume * em.bufferGain;
        targetPan = s.pan;
        targetHz = s.lowpassHz;
      }
      em.curGain += (targetGain - em.curGain) * k;
      em.curPan  += (targetPan  - em.curPan)  * k;
      em.curHz   += (targetHz   - em.curHz)   * k;
      em.gain.gain.value = em.curGain;
      em.pan.pan.value = em.curPan;
      em.lpf.frequency.value = em.curHz;
    }
  }
}

interface Emitter {
  group: string;
  src: AudioBufferSourceNode;
  gain: GainNode;
  lpf: BiquadFilterNode;
  pan: StereoPannerNode;
  /** Per-clip normalization gain from the loaded buffer. */
  bufferGain: number;
  /** Position getter — called every frame; returns null for "silent". */
  posFn: () => { x: number; z: number } | null;
  /** Caller-supplied target volume before spatial / normalization. */
  baseVolume: number;
  /** Smoothed values (chase their target each frame). */
  curGain: number;
  curPan: number;
  curHz: number;
}
